import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  Building2, Plus, Edit, Trash2, Check, X, ExternalLink, 
  Mail, Phone, DollarSign, Percent, Calendar, ChevronDown,
  ChevronUp, Copy, RefreshCw, FileText, Send
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PartnerLendersAdmin = () => {
  const { token } = useAuth();
  const [lenders, setLenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingLender, setEditingLender] = useState(null);
  const [expandedLender, setExpandedLender] = useState(null);
  const [lenderApplications, setLenderApplications] = useState({});

  const [formData, setFormData] = useState({
    name: '',
    contact_email: '',
    contact_phone: '',
    loan_types: ['auto'],
    min_loan_amount: 1000,
    max_loan_amount: 100000,
    min_apr: 5.0,
    max_apr: 25.0,
    terms_months: [36, 48, 60],
    webhook_url: '',
    is_active: true,
    auto_forward: false,
    notes: ''
  });

  useEffect(() => {
    fetchLenders();
  }, [token]);

  const fetchLenders = async () => {
    try {
      const response = await axios.get(`${API}/partner-lenders/list?active_only=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLenders(response.data.lenders || []);
    } catch (err) {
      console.error('Failed to fetch lenders:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLenderApplications = async (lenderId) => {
    try {
      const response = await axios.get(`${API}/partner-lenders/${lenderId}/applications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLenderApplications(prev => ({
        ...prev,
        [lenderId]: response.data.forwards || []
      }));
    } catch (err) {
      console.error('Failed to fetch lender applications:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLender) {
        await axios.put(`${API}/partner-lenders/${editingLender.lender_id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        const response = await axios.post(`${API}/partner-lenders/create`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Show the API key to the admin
        if (response.data.api_key) {
          alert(`Lender created successfully!\n\nAPI Key (save this - shown only once):\n${response.data.api_key}\n\nWebhook Endpoint:\n${response.data.webhook_endpoint}`);
        }
      }
      setShowAddModal(false);
      setEditingLender(null);
      resetForm();
      fetchLenders();
    } catch (err) {
      console.error('Failed to save lender:', err);
      alert('Failed to save lender: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (lenderId) => {
    if (!window.confirm('Are you sure you want to deactivate this lender?')) return;
    try {
      await axios.delete(`${API}/partner-lenders/${lenderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchLenders();
    } catch (err) {
      console.error('Failed to delete lender:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      contact_email: '',
      contact_phone: '',
      loan_types: ['auto'],
      min_loan_amount: 1000,
      max_loan_amount: 100000,
      min_apr: 5.0,
      max_apr: 25.0,
      terms_months: [36, 48, 60],
      webhook_url: '',
      is_active: true,
      auto_forward: false,
      notes: ''
    });
  };

  const openEditModal = (lender) => {
    setEditingLender(lender);
    setFormData({
      name: lender.name,
      contact_email: lender.contact_email,
      contact_phone: lender.contact_phone || '',
      loan_types: lender.loan_types,
      min_loan_amount: lender.min_loan_amount,
      max_loan_amount: lender.max_loan_amount,
      min_apr: lender.min_apr,
      max_apr: lender.max_apr,
      terms_months: lender.terms_months,
      webhook_url: lender.webhook_url || '',
      is_active: lender.is_active,
      auto_forward: lender.auto_forward,
      notes: lender.notes || ''
    });
    setShowAddModal(true);
  };

  const toggleExpand = async (lenderId) => {
    if (expandedLender === lenderId) {
      setExpandedLender(null);
    } else {
      setExpandedLender(lenderId);
      if (!lenderApplications[lenderId]) {
        await fetchLenderApplications(lenderId);
      }
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#012169]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Partner Lenders</h1>
          <p className="text-gray-500">Manage direct lender partnerships and integrations</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowAddModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-[#012169] hover:bg-[#01337d] text-white rounded-lg font-medium transition-colors"
        >
          <Plus className="w-5 h-5" />
          Add Partner Lender
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <p className="text-sm text-gray-500">Total Partners</p>
          <p className="text-2xl font-bold text-gray-900">{lenders.length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <p className="text-sm text-gray-500">Active</p>
          <p className="text-2xl font-bold text-green-600">{lenders.filter(l => l.is_active).length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <p className="text-sm text-gray-500">Total Applications</p>
          <p className="text-2xl font-bold text-blue-600">{lenders.reduce((sum, l) => sum + (l.applications_received || 0), 0)}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <p className="text-sm text-gray-500">Total Approvals</p>
          <p className="text-2xl font-bold text-purple-600">{lenders.reduce((sum, l) => sum + (l.applications_approved || 0), 0)}</p>
        </div>
      </div>

      {/* Lenders List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {lenders.length === 0 ? (
          <div className="p-12 text-center">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Partner Lenders</h3>
            <p className="text-gray-500 mb-4">Add your first lender partnership to get started.</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-[#012169] text-white rounded-lg font-medium"
            >
              Add Partner Lender
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {lenders.map((lender) => (
              <div key={lender.lender_id} className="hover:bg-gray-50 transition-colors">
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${lender.is_active ? 'bg-blue-100' : 'bg-gray-100'}`}>
                        <Building2 className={`w-6 h-6 ${lender.is_active ? 'text-blue-600' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900">{lender.name}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${lender.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                            {lender.is_active ? 'Active' : 'Inactive'}
                          </span>
                          {lender.auto_forward && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                              Auto-Forward
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {lender.contact_email}
                          </span>
                          {lender.contact_phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {lender.contact_phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Loan Types</p>
                        <p className="font-medium text-gray-900">{lender.loan_types.join(', ')}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500">APR Range</p>
                        <p className="font-medium text-gray-900">{lender.min_apr}% - {lender.max_apr}%</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Applications</p>
                        <p className="font-medium text-gray-900">{lender.applications_received || 0}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Approved</p>
                        <p className="font-medium text-green-600">{lender.applications_approved || 0}</p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleExpand(lender.lender_id)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          title="View Details"
                        >
                          {expandedLender === lender.lender_id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </button>
                        <button
                          onClick={() => openEditModal(lender)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-blue-600"
                          title="Edit"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(lender.lender_id)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-red-600"
                          title="Deactivate"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Expanded Details */}
                {expandedLender === lender.lender_id && (
                  <div className="px-4 pb-4 bg-gray-50 border-t border-gray-100">
                    <div className="grid grid-cols-3 gap-6 py-4">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Loan Parameters</h4>
                        <div className="space-y-2 text-sm">
                          <p><span className="text-gray-500">Min Amount:</span> ${lender.min_loan_amount?.toLocaleString()}</p>
                          <p><span className="text-gray-500">Max Amount:</span> ${lender.max_loan_amount?.toLocaleString()}</p>
                          <p><span className="text-gray-500">Terms:</span> {lender.terms_months?.join(', ')} months</p>
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Integration</h4>
                        <div className="space-y-2 text-sm">
                          <p><span className="text-gray-500">Webhook:</span> {lender.webhook_url || 'Not configured'}</p>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">Endpoint:</span>
                            <code className="text-xs bg-gray-200 px-2 py-0.5 rounded">/api/partner-lenders/webhook/{lender.lender_id}</code>
                            <button onClick={() => copyToClipboard(`${API}/partner-lenders/webhook/${lender.lender_id}`)} className="text-blue-600 hover:text-blue-800">
                              <Copy className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Notes</h4>
                        <p className="text-sm text-gray-600">{lender.notes || 'No notes'}</p>
                      </div>
                    </div>
                    
                    {/* Recent Applications */}
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Recent Applications</h4>
                      {lenderApplications[lender.lender_id]?.length > 0 ? (
                        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-2 text-left">Application ID</th>
                                <th className="px-4 py-2 text-left">Status</th>
                                <th className="px-4 py-2 text-left">Forwarded</th>
                                <th className="px-4 py-2 text-left">Response</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {lenderApplications[lender.lender_id].slice(0, 5).map((fwd) => (
                                <tr key={fwd.forward_id}>
                                  <td className="px-4 py-2 font-mono text-xs">{fwd.application_id}</td>
                                  <td className="px-4 py-2">
                                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                                      fwd.status === 'responded' ? 'bg-green-100 text-green-700' :
                                      fwd.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                                      'bg-yellow-100 text-yellow-700'
                                    }`}>
                                      {fwd.status}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2 text-gray-500">{new Date(fwd.forwarded_at).toLocaleDateString()}</td>
                                  <td className="px-4 py-2 text-gray-500">{fwd.response_received_at ? new Date(fwd.response_received_at).toLocaleDateString() : '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No applications forwarded yet</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {editingLender ? 'Edit Partner Lender' : 'Add Partner Lender'}
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lender Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent"
                    placeholder="e.g., ABC Auto Finance"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.contact_email}
                    onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent"
                    placeholder="partner@lender.com"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
                  <input
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Loan Types *</label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.loan_types.includes('auto')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({...formData, loan_types: [...formData.loan_types, 'auto']});
                          } else {
                            setFormData({...formData, loan_types: formData.loan_types.filter(t => t !== 'auto')});
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                      <span>Auto</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.loan_types.includes('mortgage')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({...formData, loan_types: [...formData.loan_types, 'mortgage']});
                          } else {
                            setFormData({...formData, loan_types: formData.loan_types.filter(t => t !== 'mortgage')});
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                      <span>Mortgage</span>
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min Loan Amount</label>
                  <input
                    type="number"
                    value={formData.min_loan_amount}
                    onChange={(e) => setFormData({...formData, min_loan_amount: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Loan Amount</label>
                  <input
                    type="number"
                    value={formData.max_loan_amount}
                    onChange={(e) => setFormData({...formData, max_loan_amount: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min APR (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.min_apr}
                    onChange={(e) => setFormData({...formData, min_apr: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max APR (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.max_apr}
                    onChange={(e) => setFormData({...formData, max_apr: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Webhook URL (optional)</label>
                <input
                  type="url"
                  value={formData.webhook_url}
                  onChange={(e) => setFormData({...formData, webhook_url: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent"
                  placeholder="https://lender-api.com/webhook/beth-finance"
                />
                <p className="text-xs text-gray-500 mt-1">Applications will be automatically forwarded to this URL</p>
              </div>
              
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">Active</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.auto_forward}
                    onChange={(e) => setFormData({...formData, auto_forward: e.target.checked})}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">Auto-forward applications</span>
                </label>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent"
                  rows={3}
                  placeholder="Internal notes about this partnership..."
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => { setShowAddModal(false); setEditingLender(null); resetForm(); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#012169] hover:bg-[#01337d] text-white rounded-lg font-medium"
                >
                  {editingLender ? 'Update Lender' : 'Add Lender'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerLendersAdmin;
